import Session from '@/handler/session'
import Storage from '@/handler/storageLocal'
import serviceAPI from '@/components/containers/services'

// Fábrica de listas de accesos fijados. Cada store persiste bajo su propia
// clave de localStorage y su propia key de user/preferences en el backend,
// para que dos listas independientes (ej. Favoritos globales vs. Accesos
// importantes de una vista puntual) no se pisen entre sí ni compartan datos,
// aunque comparten exactamente la misma mecánica de guardado.
export function createShortcutsStore(storageKeyPrefix, preferenceKey) {
  function getUserKey() {
    const auth = Session.getSession().auth
    const userId = auth && auth.user && auth.user.id ? auth.user.id : 'guest'
    return `${storageKeyPrefix}${userId}`
  }

  function readList() {
    const raw = Storage.getValue(getUserKey())
    if (!raw) return []
    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : []
    } catch (e) {
      return []
    }
  }

  function writeList(list) {
    Storage.setValue(getUserKey(), JSON.stringify(list))
    syncToServer(list)
  }

  function syncToServer(list) {
    try {
      var value = JSON.stringify(list)
      serviceAPI.saveUserPreference(preferenceKey, value).catch(function() {})
    } catch (e) {
      // silencioso: no bloquear la UI si falla
    }
  }

  return {
    all() {
      return readList()
    },
    has(fullPath) {
      return readList().some(item => item.fullPath === fullPath)
    },
    add(item) {
      const list = readList()
      if (list.some(entry => entry.fullPath === item.fullPath)) return list
      const next = list.concat([{
        title: item.title,
        fullPath: item.fullPath,
        // 'view' por default: es el único origen posible para quien no
        // manda type (ej. DefaultTabsLayout, que fija la tab actual).
        type: (item.type === 'action' || item.type === 'dashboard') ? item.type : 'view',
      }])
      writeList(next)
      return next
    },
    addDivider(title) {
      const list = readList()
      const id = 'divider_' + Date.now()
      const next = list.concat([{
        title: title || 'Grupo',
        type: 'divider',
        id: id,
      }])
      writeList(next)
      return next
    },
    removeByIndex(index) {
      const list = readList()
      if (index < 0 || index >= list.length) return list
      const next = list.slice()
      next.splice(index, 1)
      writeList(next)
      return next
    },
    renameByIndex(index, title) {
      const list = readList()
      if (index < 0 || index >= list.length) return list
      const next = list.map(function(item, i) {
        if (i !== index) return item
        return { ...item, title: title }
      })
      writeList(next)
      return next
    },
    moveByIndex(index, direction) {
      const list = readList()
      const targetIndex = direction === 'up' ? index - 1 : index + 1
      if (index < 0 || index >= list.length) return list
      if (targetIndex < 0 || targetIndex >= list.length) return list
      const next = list.slice()
      const temp = next[index]
      next[index] = next[targetIndex]
      next[targetIndex] = temp
      writeList(next)
      return next
    },
    remove(fullPath) {
      const list = readList()
      const next = list.filter(item => item.fullPath !== fullPath)
      writeList(next)
      return next
    },
    // Persiste un reordenamiento completo (ej. drag & drop) en vez de mover
    // de a un paso — moveByIndex/move asumen intercambios con el vecino.
    reorder(list) {
      writeList(list)
      return list
    },
    rename(fullPath, title) {
      const list = readList()
      const next = list.map(item => {
        if (item.fullPath !== fullPath) return item
        return {
          ...item,
          title: title
        }
      })
      writeList(next)
      return next
    },
    move(fullPath, direction) {
      const list = readList()
      const index = list.findIndex(item => item.fullPath === fullPath)
      if (index === -1) return list

      const targetIndex = direction === 'up' ? index - 1 : index + 1
      if (targetIndex < 0 || targetIndex >= list.length) return list

      const next = list.slice()
      const temp = next[index]
      next[index] = next[targetIndex]
      next[targetIndex] = temp

      writeList(next)
      return next
    },
    syncFromServer() {
      return serviceAPI.getUserPreference(preferenceKey)
        .then(function(response) {
          var data = response.data
          if (data && data.value) {
            try {
              var serverList = JSON.parse(data.value)
              if (Array.isArray(serverList)) {
                Storage.setValue(getUserKey(), JSON.stringify(serverList))
                return serverList
              }
            } catch (e) {
              // JSON inválido del servidor, mantener localStorage
            }
          }
          return readList()
        })
        .catch(function() {
          return readList()
        })
    }
  }
}

const shortcuts = createShortcutsStore('shortcuts_user_', 'shortcuts')

export default shortcuts
